import { useState } from "react";
import { Check, Mail, MapPin, Phone, Radio, Star, Users, X } from "lucide-react";
import { AdminPasswordConfirm } from "@/components/auth/AdminPasswordConfirm";
import { ApprovalDecisionModal } from "@/components/businesses/ApprovalDecisionModal";
import { StatusBadge } from "@/components/businesses/StatusBadge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SafeImage } from "@/components/ui/SafeImage";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { useAuth } from "@/contexts/AuthContext";
import { deriveConnectedServiceStatus } from "@/lib/business-analytics";
import {
  useApproveConnectedService,
  useRejectConnectedService,
  useSetBusinessShftOriginal,
} from "@/lib/queries";
import {
  carTitle,
  formatCurrency,
  formatDate,
  formatNumber,
  getErrorMessage,
  imageSrc,
  userDisplayName,
} from "@/lib/utils";
import type {
  Business,
  Car,
  ConnectedService,
  Customer,
  ShftServicePrice,
  User,
} from "@/types/database";

type Decision = "approve" | "reject" | "revoke";

type Props = {
  business: Business;
  connectedService: ConnectedService | null;
  prices: ShftServicePrice[];
  customers: Customer[];
  subscriberCars: Car[];
  subscriberUsers: User[];
};

export function BusinessDetailPanel({
  business,
  connectedService,
  prices,
  customers,
  subscriberCars,
  subscriberUsers,
}: Props) {
  const { adminProfile, session, verifyPassword } = useAuth();
  const approveMutation = useApproveConnectedService();
  const rejectMutation = useRejectConnectedService();
  const shftOriginalMutation = useSetBusinessShftOriginal();

  const [decision, setDecision] = useState<Decision | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [shftModalOpen, setShftModalOpen] = useState(false);
  const [shftPassword, setShftPassword] = useState("");
  const [shftError, setShftError] = useState<string | null>(null);

  const isOriginal = business.isShftOriginal === true;
  const shftPending = shftOriginalMutation.isPending;

  function closeShftModal() {
    if (shftPending) return;
    setShftModalOpen(false);
    setShftPassword("");
    setShftError(null);
  }

  async function handleToggleShftOriginal() {
    if (!shftPassword.trim()) {
      setShftError("Enter your password to confirm this change.");
      return;
    }
    setShftError(null);
    try {
      const verifyError = await verifyPassword(shftPassword);
      if (verifyError) {
        setShftError(verifyError);
        return;
      }
      await shftOriginalMutation.mutateAsync({
        id: business.id,
        isShftOriginal: !isOriginal,
      });
      setShftModalOpen(false);
      setShftPassword("");
    } catch (err) {
      setShftError(getErrorMessage(err, "Failed to update Shft Original status."));
    }
  }

  const status = deriveConnectedServiceStatus(connectedService);
  const logo = imageSrc(business.logo);
  const marketingImage = imageSrc(connectedService?.marketingImage);
  const loading = approveMutation.isPending || rejectMutation.isPending;
  const reviewer =
    adminProfile?.userID || session?.user.email || session?.user.id || "overwatch-admin";

  const usersById = new Map(
    subscriberUsers
      .filter((user) => user.userID)
      .map((user) => [user.userID as string, user])
  );

  async function handleConfirm(rejectionReason?: string) {
    if (!connectedService || !decision) return;
    setError(null);

    try {
      if (decision === "approve") {
        await approveMutation.mutateAsync({
          id: connectedService.id,
          reviewedBy: reviewer,
          businessNumericId: business.id,
        });
      } else {
        await rejectMutation.mutateAsync({
          id: connectedService.id,
          reviewedBy: reviewer,
          rejectionReason: rejectionReason || "Revoked by Overwatch admin",
          businessNumericId: business.id,
        });
      }
      setDecision(null);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update approval status."));
    }
  }

  return (
    <>
      <Workspace>
        <WorkspaceSection className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="border-b border-border/30 p-5 sm:p-6 lg:border-b-0 lg:border-r lg:border-border/30">
            <div className="flex items-start gap-4">
              {logo ? (
                <SafeImage
                  src={logo}
                  alt={business.name ?? "Business"}
                  width={72}
                  height={72}
                  className="h-16 w-16 rounded-2xl object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent-subtle text-xl font-bold text-accent-foreground">
                  {(business.name ?? "B").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                    {business.name || "Untitled business"}
                  </h1>
                  {business.isShftOriginal ? (
                    <span className="rounded-full bg-foreground px-2.5 py-0.5 text-[10px] font-bold text-accent">
                      Shft Original
                    </span>
                  ) : null}
                </div>
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant={isOriginal ? "secondary" : "default"}
                    onClick={() => {
                      setShftError(null);
                      setShftPassword("");
                      setShftModalOpen(true);
                    }}
                    disabled={shftPending}
                  >
                    <Star className="h-4 w-4" />
                    {isOriginal ? "Remove Shft Original" : "Make Shft Original"}
                  </Button>
                </div>
                <p className="mt-1 text-sm font-semibold text-muted">
                  {business.type || "Unspecified type"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <StatusBadge status={status} />
                  <span className="stat-pill">
                    <Users className="h-3.5 w-3.5" />
                    {formatNumber(business.users?.length ?? 0)} team
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              {business.email ? (
                <p className="flex items-center gap-2 text-muted">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="font-semibold text-foreground">{business.email}</span>
                </p>
              ) : null}
              {business.phone != null ? (
                <p className="flex items-center gap-2 text-muted">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span className="font-semibold text-foreground">{business.phone}</span>
                </p>
              ) : null}
              <p className="flex items-start gap-2 text-muted">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="font-semibold text-foreground">
                  {[business.address, business.country].filter(Boolean).join(" · ") || "No address"}
                </span>
              </p>
              {business.slug ? (
                <p className="text-xs font-semibold text-muted">Slug · {business.slug}</p>
              ) : null}
              {business.businessID ? (
                <p className="truncate text-xs font-semibold text-muted">
                  Business ID · {business.businessID}
                </p>
              ) : (
                <p className="text-xs font-semibold text-orange-700">
                  Missing businessID — related connected-service data cannot be linked.
                </p>
              )}
            </div>
          </div>

          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted">
                  Connected services
                </p>
                <p className="mt-1 text-sm font-semibold text-muted">
                  {connectedService
                    ? "Application and live branding from the Shft Control app"
                    : "This business has not applied yet"}
                </p>
              </div>
              <Radio className="h-5 w-5 text-muted" />
            </div>

            {error ? <p className="mt-3 text-sm font-semibold text-red-600">{error}</p> : null}

            {connectedService ? (
              <div className="mt-4 space-y-4">
                <div className="rounded-2xl bg-canvas p-4">
                  <div className="flex items-center justify-between gap-2">
                    <StatusBadge status={status} />
                    <p className="text-xs text-muted">
                      Applied {formatDate(connectedService.created_at)}
                    </p>
                  </div>
                  {connectedService.reviewedAt ? (
                    <p className="mt-2 text-xs text-muted">
                      Reviewed {formatDate(connectedService.reviewedAt)}
                      {connectedService.reviewedBy ? ` by ${connectedService.reviewedBy}` : ""}
                    </p>
                  ) : null}
                  {connectedService.rejectionReason ? (
                    <p className="mt-2 text-xs font-semibold text-orange-700">
                      Reason: {connectedService.rejectionReason}
                    </p>
                  ) : null}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {status === "pending" || status === "rejected" ? (
                      <Button size="sm" variant="brand" onClick={() => setDecision("approve")} disabled={loading}>
                        <Check className="h-4 w-4" />
                        Approve
                      </Button>
                    ) : null}
                    {status === "pending" ? (
                      <Button size="sm" variant="danger" onClick={() => setDecision("reject")} disabled={loading}>
                        <X className="h-4 w-4" />
                        Reject
                      </Button>
                    ) : null}
                    {status === "approved" ? (
                      <Button size="sm" variant="secondary" onClick={() => setDecision("revoke")} disabled={loading}>
                        Revoke
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-border/40 bg-surface">
                  <div className="flex items-center gap-3 p-3">
                    {imageSrc(connectedService.businessLogo) ? (
                      <SafeImage
                        src={imageSrc(connectedService.businessLogo)!}
                        alt=""
                        width={40}
                        height={40}
                        className="h-10 w-10 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-canvas text-xs font-bold">
                        {(connectedService.businessName ?? "S").charAt(0)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold">
                        {connectedService.businessName || business.name}
                      </p>
                      <p className="text-xs text-muted">{connectedService.businessType || business.type}</p>
                    </div>
                  </div>
                  <div className="relative h-36 bg-canvas">
                    {marketingImage ? (
                      <SafeImage
                        src={marketingImage}
                        alt="Marketing"
                        width={640}
                        height={240}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-muted">
                        No marketing image
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-xs font-medium leading-snug text-white">
                        {connectedService.promotionalText || "No promotional text yet."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-dashed border-border bg-canvas p-6 text-center">
                <p className="text-sm font-semibold text-muted">
                  No Connected Services application on file.
                </p>
              </div>
            )}
          </div>
        </WorkspaceSection>

        <WorkspaceSection border className="p-5 sm:p-6">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Service pricing</p>
          <p className="mt-1 text-sm text-muted">
            Items this business offers inside Connected Services
          </p>
          {prices.length === 0 ? (
            <p className="mt-4 text-sm text-muted">No priced services yet.</p>
          ) : (
            <div className="mt-4 divide-y divide-border/40 overflow-hidden rounded-2xl bg-canvas">
              {prices.map((price) => (
                <div key={price.shftpriceID} className="flex items-start justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="font-bold text-foreground">{price.serviceName || "Untitled service"}</p>
                    {price.description ? (
                      <p className="mt-1 text-xs text-muted">{price.description}</p>
                    ) : null}
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-muted">
                      {price.pricingUnit || "Flat Rate"}
                      {price.isAvailable === false ? " · Unavailable" : ""}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-extrabold">
                    {formatCurrency(price.Price)}
                    {price.currency ? (
                      <span className="ml-1 text-xs font-semibold text-muted">{price.currency}</span>
                    ) : null}
                  </p>
                </div>
              ))}
            </div>
          )}
        </WorkspaceSection>

        <WorkspaceSection border className="grid gap-0 lg:grid-cols-2">
          <div className="border-b border-border/30 p-5 sm:p-6 lg:border-b-0 lg:border-r lg:border-border/30">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Customers</p>
            <p className="mt-1 text-sm text-muted">{formatNumber(customers.length)} on file</p>
            {customers.length === 0 ? (
              <p className="mt-4 text-sm text-muted">No customers linked yet.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {customers.slice(0, 8).map((customer) => (
                  <div key={customer.id} className="rounded-2xl bg-canvas p-3">
                    <p className="text-sm font-bold">
                      {[customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
                        "Unnamed customer"}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-muted">
                      {customer.email || customer.country || "No contact"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-5 sm:p-6">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Subscribers</p>
            <p className="mt-1 text-sm text-muted">
              {formatNumber(subscriberCars.length)} cars linked to this connected service
            </p>
            {subscriberCars.length === 0 ? (
              <p className="mt-4 text-sm text-muted">No subscribers yet.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {subscriberCars.slice(0, 8).map((car) => {
                  const owner = usersById.get(car.userID);
                  return (
                    <div key={car.carID} className="rounded-2xl bg-canvas p-3">
                      <p className="text-sm font-bold">{carTitle(car)}</p>
                      <p className="mt-0.5 text-xs text-muted">
                        {owner ? userDisplayName(owner) : car.userID}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </WorkspaceSection>
      </Workspace>

      <ApprovalDecisionModal
        open={Boolean(decision && connectedService)}
        decision={decision}
        service={connectedService}
        loading={loading}
        onClose={() => {
          if (loading) return;
          setDecision(null);
        }}
        onConfirm={(reason) => void handleConfirm(reason)}
      />

      <Modal
        open={shftModalOpen}
        onClose={closeShftModal}
        title={isOriginal ? "Remove Shft Original" : "Make Shft Original"}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={closeShftModal} disabled={shftPending}>
              Cancel
            </Button>
            <Button
              variant={isOriginal ? "danger" : "brand"}
              size="sm"
              onClick={() => void handleToggleShftOriginal()}
              disabled={!shftPassword.trim() || shftPending}
            >
              {shftPending
                ? "Saving…"
                : isOriginal
                  ? "Remove Shft Original"
                  : "Make Shft Original"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p>
            {isOriginal
              ? `This will remove the Shft Original designation from ${business.name || "this business"}.`
              : `This will mark ${business.name || "this business"} as a Shft Original.`}
          </p>
          <AdminPasswordConfirm
            password={shftPassword}
            onPasswordChange={setShftPassword}
            disabled={shftPending}
          />
          {shftError ? <p className="text-sm font-semibold text-red-600">{shftError}</p> : null}
        </div>
      </Modal>
    </>
  );
}
